<template>
  <div class="change-password">
    <div class="card change-password__card">
      <img
        class="vm-logo change-password__logo"
        src="../assets/images/logo-videomedi.png"
        alt="Videomedi"
      />
      <h1 class="change-password__title">Crie sua senha</h1>
      <p class="change-password__text">
        Você entrou com uma senha provisória. Defina uma nova senha para
        continuar.
      </p>

      <form class="change-password__form" @submit.prevent="submit">
        <div class="field">
          <label class="label" for="cp-current">Senha provisória</label>
          <input
            id="cp-current"
            v-model="current"
            class="input"
            type="password"
            autocomplete="current-password"
          />
        </div>
        <div class="field">
          <label class="label" for="cp-new">Nova senha</label>
          <input
            id="cp-new"
            v-model="password"
            class="input"
            type="password"
            autocomplete="new-password"
          />
          <ul class="change-password__rules">
            <li
              v-for="rule in rules"
              :key="rule.key"
              :class="{ ok: rule.test(password) }"
            >
              {{ rule.test(password) ? "✓" : "•" }} {{ rule.label }}
            </li>
          </ul>
        </div>
        <div class="field">
          <label class="label" for="cp-confirm">Confirmar nova senha</label>
          <input
            id="cp-confirm"
            v-model="confirmation"
            class="input"
            type="password"
            autocomplete="new-password"
          />
          <span v-if="mismatch" class="change-password__error">
            As senhas não conferem.
          </span>
        </div>
        <button
          class="btn btn--primary change-password__submit"
          type="submit"
          :disabled="!canSubmit || saving"
        >
          {{ saving ? "Salvando…" : "Salvar e continuar" }}
        </button>
        <p v-if="error" class="change-password__error" data-test="error">
          {{ error }}
        </p>
      </form>
    </div>
  </div>
</template>

<script>
import { Account } from "@/services/account";
import { PASSWORD_RULES, isPasswordValid } from "@/utils/passwordRules";

// Troca obrigatória da senha provisória (US-C12, flow-c20-troca-senha.md).
export default {
  name: "ChangePasswordView",
  data() {
    return {
      current: "",
      password: "",
      confirmation: "",
      saving: false,
      error: "",
      rules: PASSWORD_RULES
    };
  },
  methods: {
    async submit() {
      if (!this.canSubmit || this.saving) return;
      this.saving = true;
      this.error = "";
      try {
        await Account.changePassword({
          currentPassword: this.current,
          password: this.password,
          passwordConfirmation: this.confirmation
        });
        // O usuário do login guarda a flag e as unidades (users#show não traz).
        const user = this.$store.state.auth.loggedUser || {};
        this.$store.commit("auth/SET_LOGGED_USER", {
          ...user,
          must_change_password: false
        });
        this.$toast.success("Senha atualizada");
        this.$router.replace(this.destination(user));
      } catch (e) {
        const body = e && e.response && e.response.data;
        this.error = (body && body.error) || "Não foi possível trocar a senha.";
      } finally {
        this.saving = false;
      }
    },
    // Conta de equipe de clínica vai para o módulo Clínica.
    destination(user) {
      const staff = (user.unit_ids || []).length > 0;
      return staff ? "/clinica" : "/";
    }
  },
  computed: {
    mismatch() {
      return !!this.confirmation && this.confirmation !== this.password;
    },
    canSubmit() {
      return (
        !!this.current &&
        isPasswordValid(this.password) &&
        this.confirmation === this.password
      );
    }
  }
};
</script>

<style lang="scss" scoped>
.change-password {
  display: flex;
  justify-content: center;
  padding: var(--space-12) var(--space-4);
  background: var(--color-bg-page);
  min-height: 100vh;
}
.change-password__card {
  width: 100%;
  max-width: 440px;
  padding: var(--space-8);
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}
.change-password__logo {
  height: 32px;
  align-self: flex-start;
}
.change-password__title {
  margin: 0;
  font-size: var(--font-size-2xl);
  font-weight: var(--fw-bold);
  color: var(--color-gray-900);
}
.change-password__text {
  margin: 0;
  font-size: var(--font-size-sm);
  color: var(--color-gray-500);
}
.change-password__form {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}
.change-password__rules {
  list-style: none;
  margin: var(--space-2) 0 0;
  padding: 0;
  font-size: var(--font-size-xs);
  color: var(--color-gray-500);

  li.ok {
    color: var(--color-success);
  }
}
.change-password__error {
  font-size: var(--font-size-xs);
  color: var(--color-danger);
}
.change-password__submit {
  width: 100%;
}
</style>
